/**
 * Stylesheet order with code-split pages (ADR 0042).
 *
 * The app's CSS is written for one cascade: the styles of pages and components come first, the
 * global sheets of main.tsx (tokens, base, app … platform, organize) come last — `platform.css`
 * in particular re-colours page-specific rules for the themes at the same specificity, and only
 * wins because it is later. That order used to fall out of the single bundle (`import { App }`
 * precedes the global style imports).
 *
 * With one chunk per page, a page's stylesheet is attached when the page is first opened — by
 * default at the END of <head>, after the globals, which silently flips every such override (the
 * dark theme lost its form colours on the sign-in page, for one). So every stylesheet that is
 * attached later is placed BEFORE the app stylesheet instead: pages first, globals last, exactly
 * as before. Nothing is moved after the fact (a moved <link> would be applied twice).
 *
 * `tests/e2e/style-order.spec.ts` and `theme.spec.ts` guard this.
 */
export function keepPageStylesFirst(doc: Document = document): void {
  const head = doc.head;
  if (import.meta.env.DEV) return keepDevStylesFirst(head);
  // index.html lists the sheets of the entry's own chunks, the global one last (build/delivery.ts
  // `entryStylesheetLast`). Everything attached later goes in front of all of them.
  const entry = head.querySelector<HTMLLinkElement>('link[rel="stylesheet"]');
  if (!entry) return;
  const append = head.appendChild.bind(head) as <T extends Node>(node: T) => T;
  // Vite's chunk loader attaches a page's stylesheet with `document.head.appendChild(link)`.
  head.appendChild = function appendKeepingOrder<T extends Node>(node: T): T {
    if (node instanceof HTMLLinkElement && node.rel === 'stylesheet' && entry.parentNode === head) return head.insertBefore(node, entry);
    return append(node);
  };
}

/**
 * Development server: styles are <style> tags added as modules run, so a lazily loaded page's
 * styles would also land after the globals. The first global sheet is the anchor; anything the
 * dev server adds later goes in front of it (same cascade as the production build).
 */
function keepDevStylesFirst(head: HTMLHeadElement): void {
  const anchorOf = () => head.querySelector<HTMLStyleElement>('style[data-vite-dev-id*="noto-sans-thai"], style[data-vite-dev-id$="styles/tokens.css"]');
  new MutationObserver((records) => {
    const anchor = anchorOf();
    if (!anchor) return;
    for (const r of records) {
      for (const node of r.addedNodes) {
        if (!(node instanceof HTMLStyleElement) || !node.dataset.viteDevId) continue;
        // Already in front of the globals (hot updates replace a sheet in place).
        if (anchor.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_PRECEDING) continue;
        head.insertBefore(node, anchor);
      }
    }
  }).observe(head, { childList: true });
}
